import React, { useState } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
} from '@mui/material';

import { TechStackItem, TechStackResult } from 'stores/ProjectStoreAdapter';

type TechCategory = 'TECH_LAYER' | 'TECH_TYPE';

type TechStackSelectProps = {
  /** 技術スタックデータ（API取得結果） */
  techStackData: TechStackResult | null;
  /** 選択中の技術スタックIDリスト */
  selectedIds: string[];
  /** 選択変更ハンドラ */
  onChange: (ids: string[]) => void;
  /** 無効状態 */
  disabled?: boolean;
};

/**
 * 技術スタック選択コンポーネント。
 *
 * - ラジオボタンで分類（技術レイヤー / 技術種別）を切り替え
 * - 切り替えた分類ごとにグループ化された技術スタックを表示
 * - グルーピングマルチセレクトボックスを使用
 * - 選択状態は分類間で共有（同じ技術スタックIDは1つ）
 */
const TechStackSelect: React.FC<TechStackSelectProps> = ({
  techStackData,
  selectedIds,
  onChange,
  disabled = false,
}) => {
  const [techCategory, setTechCategory] = useState<TechCategory>('TECH_LAYER');

  // 技術スタックのグループ化
  const getTechStackOptions = (): { group: string; items: TechStackItem[] }[] => {
    if (!techStackData) return [];
    const list = techStackData[techCategory] || [];
    const groups: Record<string, TechStackItem[]> = {};
    list.forEach((item) => {
      const groupKey = item.categoryLabel;
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(item);
    });
    return Object.entries(groups).map(([group, items]) => ({ group, items }));
  };

  const handleToggle = (techStackId: string) => {
    if (selectedIds.includes(techStackId)) {
      onChange(selectedIds.filter((id) => id !== techStackId));
    } else {
      onChange([...selectedIds, techStackId]);
    }
  };

  // 全アイテムからIDで名前を取得（renderValue用）
  const getAllItems = (): TechStackItem[] => {
    if (!techStackData) return [];
    return [...(techStackData.TECH_LAYER || []), ...(techStackData.TECH_TYPE || [])];
  };

  return (
    <>
      <RadioGroup
        row
        value={techCategory}
        onChange={(e) => setTechCategory(e.target.value as TechCategory)}
        sx={{ mb: 1 }}
      >
        <FormControlLabel
          value="TECH_LAYER"
          control={<Radio size="small" />}
          label="技術レイヤー"
          disabled={disabled}
        />
        <FormControlLabel
          value="TECH_TYPE"
          control={<Radio size="small" />}
          label="技術種別"
          disabled={disabled}
        />
      </RadioGroup>
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>技術スタック</InputLabel>
        <Select
          multiple
          value={selectedIds}
          label="技術スタック"
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 350,
                border: '1px solid',
                borderColor: 'divider',
              },
            },
          }}
          renderValue={(selected) => {
            const allItems = getAllItems();
            const uniqueIds = Array.from(new Set(selected));
            const idNamePairs = uniqueIds
              .map((id) => {
                const item = allItems.find((item) => item.techStackId === id);
                return item ? { id, name: item.techStackName } : null;
              })
              .filter((pair): pair is { id: string; name: string } => !!pair);
            return (
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {idNamePairs.map(({ id, name }) => (
                  <Chip
                    key={id}
                    label={name}
                    size="small"
                    {...(!disabled && {
                      onDelete: (e: React.SyntheticEvent) => {
                        e.stopPropagation();
                        onChange(selectedIds.filter((sid) => sid !== id));
                      },
                    })}
                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                ))}
              </Stack>
            );
          }}
          sx={{ borderRadius: 2 }}
        >
          {getTechStackOptions().map(({ group, items }) => [
            <ListSubheader
              key={group}
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                bgcolor: 'background.paper',
                lineHeight: '32px',
              }}
            >
              {group}
            </ListSubheader>,
            ...items.map((item) => (
              <MenuItem
                key={`${item.techStackId}-${item.categoryCode}`}
                value={item.techStackId}
                onClick={() => handleToggle(item.techStackId)}
              >
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selectedIds.includes(item.techStackId)}
                  size="small"
                  sx={{ mr: 1 }}
                />
                {item.techStackName}
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>
    </>
  );
};

export default TechStackSelect;
